// Maps JavaScript API map for sections/map.liquid. The API is only loaded once the map nears the
// viewport, and is shared by every <interactive-map> on the page. If the key is rejected or the
// address can't be found, we swap in Google's keyless embed so the section never shows a blank box.

const MAP_STYLES = {
  standard: [],
  light: [
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#e9e9e9' }, { lightness: 17 }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f0f0f0' }, { lightness: 20 }] },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }, { lightness: 17 }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { lightness: 18 }] },
    { featureType: 'road.local', elementType: 'geometry', stylers: [{ color: '#ffffff' }, { lightness: 16 }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#f0f0f0' }, { lightness: 21 }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dedede' }, { lightness: 21 }] },
    { elementType: 'labels.text.stroke', stylers: [{ visibility: 'on' }, { color: '#ffffff' }, { lightness: 16 }] },
    { elementType: 'labels.text.fill', stylers: [{ saturation: 36 }, { color: '#333333' }, { lightness: 40 }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#f2f2f2' }, { lightness: 19 }] },
    { featureType: 'administrative', elementType: 'geometry.fill', stylers: [{ color: '#fefefe' }, { lightness: 20 }] },
    { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 }] },
  ],
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#212121' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
    { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#262626' }] },
    { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] },
    { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
    { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] },
    { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] },
  ],
};

let apiPromise;

// Google calls window.gm_authFailure (not a rejected promise) when the key is refused,
// so every map on the page listens for this event to fall back.
function loadMapsApi(apiKey) {
  if (window.google?.maps?.Map) return Promise.resolve();
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    window.__interactiveMapReady = resolve;
    window.gm_authFailure = () => document.dispatchEvent(new CustomEvent('interactive-map:auth-failure'));

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&loading=async&callback=__interactiveMapReady`;
    script.async = true;
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return apiPromise;
}

// Geocoding is billed per request, so remember each address' coordinates in this browser.
async function geocode(address) {
  const cacheKey = `interactive-map:${address}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch (e) {}

  const { results } = await new google.maps.Geocoder().geocode({ address });
  if (!results?.length) throw new Error(`No geocoding result for "${address}"`);

  const location = results[0].geometry.location.toJSON();
  try {
    localStorage.setItem(cacheKey, JSON.stringify(location));
  } catch (e) {}
  return location;
}

function pinIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="50" viewBox="0 0 34 50"><path fill="${color}" d="M17 0C7.6 0 0 7.6 0 17c0 12.8 17 33 17 33s17-20.2 17-33C34 7.6 26.4 0 17 0z"/><circle cx="17" cy="17" r="6.5" fill="#fff"/></svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(34, 50),
    anchor: new google.maps.Point(17, 50),
  };
}

class InteractiveMap extends HTMLElement {
  connectedCallback() {
    this.onAuthFailure = () => this.fallback();
    document.addEventListener('interactive-map:auth-failure', this.onAuthFailure);

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.render();
      },
      { rootMargin: '300px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('interactive-map:auth-failure', this.onAuthFailure);
  }

  async render() {
    const address = this.getAttribute('map-address');
    try {
      await loadMapsApi(this.getAttribute('api-key'));
      const position = await geocode(address);

      const map = new google.maps.Map(this, {
        center: position,
        zoom: Number(this.getAttribute('map-zoom')) || 14,
        styles: MAP_STYLES[this.getAttribute('map-style')] || [],
        disableDefaultUI: true,
        zoomControl: true,
        fullscreenControl: true,
        gestureHandling: 'cooperative',
      });

      new google.maps.Marker({
        map,
        position,
        title: address,
        icon: pinIcon(this.getAttribute('pin-color') || '#212121'),
      });
    } catch (error) {
      console.error('[interactive-map]', error);
      this.fallback();
    }
  }

  fallback() {
    if (this.dataset.fallback) return;
    this.dataset.fallback = 'true';

    const iframe = document.createElement('iframe');
    iframe.src = `https://maps.google.com/maps?q=${encodeURIComponent(this.getAttribute('map-address'))}&output=embed`;
    iframe.title = this.getAttribute('map-title') || '';
    iframe.loading = 'lazy';
    iframe.referrerPolicy = 'no-referrer-when-downgrade';
    this.replaceChildren(iframe);
  }
}

if (!customElements.get('interactive-map')) customElements.define('interactive-map', InteractiveMap);
